import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Eye, FileText, Loader2, Megaphone, RefreshCw, Search, Send, X } from "lucide-react";
import toast from "react-hot-toast";
import axiosInstance from "../../utils/axiosInstance";
import { fetchAllCustomers, formatDateTime, getSmsMetrics, normalizeMobile, parseCustomNumbers, rowsFrom } from "./smsCampaignUtils";

const statusStyle = {
  sent: ["ارسال شده", "bg-emerald-50 text-emerald-700"],
  queued: ["در صف", "bg-amber-50 text-amber-700"],
  failed: ["ناموفق", "bg-rose-50 text-rose-700"],
};

export default function AdminSmsCampaignSection() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [templateId, setTemplateId] = useState("");
  const [message, setMessage] = useState("");
  const [customNumbers, setCustomNumbers] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadData = async (initial = true) => {
    initial ? setLoading(true) : setRefreshing(true);
    try {
      const [templateResponse, campaignResponse, customerRows] = await Promise.all([
        axiosInstance.get("/notifications/sms-templates/"),
        axiosInstance.get("/notifications/sms-campaigns/"),
        fetchAllCustomers(axiosInstance),
      ]);
      const templateRows = rowsFrom(templateResponse);
      setTemplates(templateRows);
      setCampaigns(rowsFrom(campaignResponse));
      setCustomers(customerRows);
      const storedId = sessionStorage.getItem("sms_campaign_template_id");
      const stored = templateRows.find((item) => String(item.id) === storedId);
      if (stored) {
        setTemplateId(String(stored.id));
        setMessage(stored.message);
        sessionStorage.removeItem("sms_campaign_template_id");
      }
    } catch (error) {
      toast.error("دریافت اطلاعات ارسال پیامک ناموفق بود.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    if (!term) return customers;
    return customers.filter((customer) =>
      [customer.full_name, customer.phone_number, customer.national_code]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(term)),
    );
  }, [customers, search]);

  const parsedNumbers = useMemo(() => parseCustomNumbers(customNumbers), [customNumbers]);
  const invalidNumbers = useMemo(() => parsedNumbers.filter((value) => !normalizeMobile(value)), [parsedNumbers]);
  const metrics = useMemo(() => getSmsMetrics(message), [message]);
  const recipientNumbers = useMemo(() => {
    const result = new Set(parsedNumbers.map(normalizeMobile).filter(Boolean));
    customers.forEach((customer) => {
      if (selectedIds.has(customer.id)) result.add(normalizeMobile(customer.phone_number));
    });
    result.delete("");
    return result;
  }, [customers, parsedNumbers, selectedIds]);

  const selectTemplate = (id) => {
    setTemplateId(id);
    const template = templates.find((item) => String(item.id) === id);
    if (template) setMessage(template.message);
  };

  const toggleCustomer = (customer) => {
    if (!normalizeMobile(customer.phone_number)) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(customer.id) ? next.delete(customer.id) : next.add(customer.id);
      return next;
    });
  };

  const toggleVisible = () => {
    const ids = filteredCustomers.filter((item) => normalizeMobile(item.phone_number)).map((item) => item.id);
    const remove = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    setSelectedIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => remove ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const review = () => {
    if (!message.trim()) return toast.error("متن پیام را وارد کنید.");
    if (!recipientNumbers.size) return toast.error("حداقل یک گیرنده انتخاب کنید.");
    if (invalidNumbers.length) return toast.error("شماره‌های دستی نامعتبر را اصلاح کنید.");
    setConfirmOpen(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const response = await axiosInstance.post("/notifications/sms-campaigns/send/", {
        template_id: templateId ? Number(templateId) : null,
        message: message.trim(),
        customer_ids: Array.from(selectedIds),
        custom_numbers: parsedNumbers,
        confirmed: true,
      });
      setCampaigns((current) => [response.data, ...current]);
      setSelectedIds(new Set());
      setCustomNumbers("");
      setConfirmOpen(false);
      toast.success(String(response.data.recipient_count) + " پیامک در صف ارسال قرار گرفت.");
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.custom_numbers?.[0] || data?.customer_ids?.[0] || data?.recipients?.[0] || data?.message?.[0] || "ایجاد ارسال گروهی ناموفق بود.");
    } finally { setSending(false); }
  };

  const openDetails = async (id) => {
    setDetails({ id });
    setDetailsLoading(true);
    try {
      const response = await axiosInstance.get("/notifications/sms-campaigns/" + id + "/");
      setDetails(response.data);
    } catch (error) {
      setDetails(null);
      toast.error("دریافت جزئیات ارسال ناموفق بود.");
    } finally { setDetailsLoading(false); }
  };

  if (loading) return <div className="flex min-h-64 items-center justify-center text-sm text-slate-500"><Loader2 className="ml-2 h-5 w-5 animate-spin" />در حال دریافت اطلاعات پیامک</div>;

  return (
    <section className="space-y-6" dir="rtl">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white"><Megaphone className="h-5 w-5" /></span>
          <div><h2 className="text-lg font-bold text-slate-900">ارسال پیامک تبلیغاتی</h2><p className="mt-1 text-sm text-slate-500">ساخت پیام، انتخاب گیرندگان و پیگیری نتیجه ارسال</p></div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => navigate("/adminpanel/sms-templates")} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><FileText className="h-4 w-4" />مدیریت قالب‌ها</button>
          <button type="button" title="بروزرسانی" onClick={() => loadData(false)} disabled={refreshing} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"><RefreshCw className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")} /></button>
        </div>
      </header>

      <div className="grid overflow-hidden rounded-lg border border-slate-200 bg-white lg:grid-cols-[minmax(0,1fr)_23rem]">
        <div className="space-y-6 p-4 sm:p-6">
          <StepHeader number="۱" title="متن پیام" color="emerald" />
          <div>
            <div className="mb-2 flex items-center justify-between gap-3"><label htmlFor="campaign-template" className="text-sm font-medium text-slate-700">قالب</label><button type="button" onClick={() => navigate("/adminpanel/sms-templates")} className="text-xs font-semibold text-sky-700">ایجاد یا ویرایش قالب</button></div>
            <select id="campaign-template" value={templateId} onChange={(event) => selectTemplate(event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100">
              <option value="">پیام بدون قالب</option>
              {templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between gap-3"><label htmlFor="campaign-message" className="text-sm font-medium text-slate-700">متن پیامک</label><span className="whitespace-nowrap text-xs text-slate-500">{metrics.characters} نویسه، {metrics.segments} پیامک</span></div>
            <textarea id="campaign-message" value={message} onChange={(event) => setMessage(event.target.value.slice(0, 1000))} rows={9} placeholder="متن پیام تبلیغاتی را وارد کنید..." className="w-full resize-y rounded-lg border border-slate-300 px-3 py-3 text-sm leading-7 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" />
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-sky-500" style={{ width: Math.min(metrics.characters / 10, 100) + "%" }} /></div>
          </div>
        </div>

        <div className="space-y-5 border-t border-slate-200 bg-slate-50/50 p-4 sm:p-6 lg:border-r lg:border-t-0">
          <StepHeader number="۲" title="گیرندگان" color="sky" aside={String(recipientNumbers.size) + " شماره یکتا"} />
          <label className="relative block"><Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="جستجوی نام، شماره یا کد ملی" className="w-full rounded-lg border border-slate-300 py-2.5 pr-9 pl-3 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" /></label>
          <div className="flex justify-between text-xs"><span className="text-slate-500">{selectedIds.size} مشتری انتخاب شده</span><button type="button" onClick={toggleVisible} className="font-semibold text-sky-700">انتخاب همه نتایج</button></div>
          <div className="h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white">
            {filteredCustomers.length ? filteredCustomers.map((customer) => {
              const valid = Boolean(normalizeMobile(customer.phone_number));
              return <label key={customer.id} className={"flex min-h-12 items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-0 " + (valid ? "cursor-pointer hover:bg-sky-50/40" : "cursor-not-allowed opacity-50")}><input type="checkbox" checked={selectedIds.has(customer.id)} disabled={!valid} onChange={() => toggleCustomer(customer)} className="h-4 w-4 shrink-0 rounded border-slate-300 text-sky-600" /><span className="min-w-0 flex-1 truncate text-sm text-slate-800">{customer.full_name}</span><span className="shrink-0 text-xs text-slate-500" dir="ltr">{customer.phone_number || "بدون شماره"}</span></label>;
            }) : <p className="py-10 text-center text-sm text-slate-500">مشتری‌ای پیدا نشد.</p>}
          </div>
          <div><label htmlFor="custom-numbers" className="mb-2 block text-sm font-medium text-slate-700">شماره‌های دیگر</label><textarea id="custom-numbers" value={customNumbers} onChange={(event) => setCustomNumbers(event.target.value)} rows={3} dir="ltr" placeholder={"09121234567\n09211234567"} className={"w-full resize-y rounded-lg border bg-white px-3 py-2 text-sm outline-none " + (invalidNumbers.length ? "border-rose-400 focus:ring-2 focus:ring-rose-100" : "border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100")} />{invalidNumbers.length > 0 && <p className="mt-1 text-xs text-rose-600">{invalidNumbers.length} شماره معتبر نیست.</p>}</div>
          <button type="button" onClick={review} disabled={!message.trim() || !recipientNumbers.size || invalidNumbers.length > 0} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"><Send className="h-4 w-4" />بررسی و ارسال</button>
        </div>
      </div>

      <History campaigns={campaigns} onOpen={openDetails} />
      {confirmOpen && <ConfirmModal message={message} metrics={metrics} count={recipientNumbers.size} sending={sending} onClose={() => setConfirmOpen(false)} onConfirm={send} />}
      {details && <DetailsModal campaign={details} loading={detailsLoading} onClose={() => setDetails(null)} />}
    </section>
  );
}

function StepHeader({ number, title, color, aside }) {
  const numberClass = color === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700";
  return <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-3"><div className="flex items-center gap-2"><span className={"inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold " + numberClass}>{number}</span><h3 className="text-sm font-bold text-slate-900">{title}</h3></div>{aside && <span className="text-xs font-semibold text-slate-600">{aside}</span>}</div>;
}

function History({ campaigns, onOpen }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 sm:px-6"><div><h3 className="text-sm font-bold text-slate-900">تاریخچه ارسال</h3><p className="mt-1 text-xs text-slate-500">وضعیت صف و نتیجه تحویل هر کمپین</p></div><span className="text-xs text-slate-500">{campaigns.length} ارسال</span></div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-xs font-semibold text-slate-600"><tr><th className="px-4 py-3 text-right sm:px-6">ارسال</th><th className="px-4 py-3 text-right">قالب و متن</th><th className="px-4 py-3 text-center">کل</th><th className="px-4 py-3 text-center">ارسال شده</th><th className="px-4 py-3 text-center">در صف</th><th className="px-4 py-3 text-center">ناموفق</th><th className="px-4 py-3 text-left sm:px-6">جزئیات</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{campaigns.map((campaign) => <tr key={campaign.id} className="hover:bg-slate-50/70"><td className="whitespace-nowrap px-4 py-3 sm:px-6"><p className="font-semibold text-slate-800">شماره {campaign.id}</p><p className="mt-1 text-xs text-slate-500">{formatDateTime(campaign.created_at)}</p></td><td className="max-w-xs px-4 py-3"><p className="text-xs font-semibold text-slate-700">{campaign.template_name || "بدون قالب"}</p><p className="mt-1 truncate text-xs text-slate-500">{campaign.message}</p></td><td className="px-4 py-3 text-center font-semibold text-slate-700">{campaign.recipient_count}</td><td className="px-4 py-3 text-center text-emerald-700">{campaign.sent_count}</td><td className="px-4 py-3 text-center text-amber-700">{campaign.queued_count}</td><td className="px-4 py-3 text-center text-rose-700">{campaign.failed_count}</td><td className="px-4 py-3 text-left sm:px-6"><button type="button" title="مشاهده جزئیات" onClick={() => onOpen(campaign.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-sky-50 hover:text-sky-700"><Eye className="h-4 w-4" /></button></td></tr>)}</tbody>
        </table>
        {!campaigns.length && <p className="py-10 text-center text-sm text-slate-500">هنوز ارسالی ثبت نشده است.</p>}
      </div>
    </div>
  );
}

function ConfirmModal({ message, metrics, count, sending, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" onMouseDown={onClose}>
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4"><div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /><h3 className="font-bold text-slate-900">تایید ارسال پیامک</h3></div><CloseButton onClick={onClose} /></div>
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-3 divide-x divide-x-reverse divide-slate-200 rounded-lg border border-slate-200 bg-slate-50 text-center"><Metric value={count} label="گیرنده" /><Metric value={metrics.segments} label="بخش پیام" /><Metric value={count * metrics.segments} label="پیامک تقریبی" /></div>
          <p className="max-h-28 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm leading-6 text-slate-700">{message}</p>
          <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">انصراف</button><button type="button" onClick={onConfirm} disabled={sending} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{sending ? "در حال ارسال" : "تایید و ارسال"}</button></div>
        </div>
      </div>
    </div>
  );
}

function Metric({ value, label }) {
  return <div className="p-3"><p className="text-lg font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>;
}

function CloseButton({ onClick }) {
  return <button type="button" title="بستن" onClick={onClick} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>;
}

function DetailsModal({ campaign, loading, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" onMouseDown={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4"><div><h3 className="font-bold text-slate-900">جزئیات ارسال شماره {campaign.id}</h3>{campaign.created_at && <p className="mt-1 text-xs text-slate-500">{formatDateTime(campaign.created_at)}</p>}</div><CloseButton onClick={onClose} /></div>
        {loading ? <div className="flex min-h-52 items-center justify-center text-sm text-slate-500"><Loader2 className="ml-2 h-5 w-5 animate-spin" />در حال دریافت جزئیات</div> : <div className="overflow-auto">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-4"><p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{campaign.message}</p></div>
          <table className="w-full min-w-[680px] text-sm"><thead className="text-xs text-slate-600"><tr><th className="px-5 py-3 text-right">گیرنده</th><th className="px-4 py-3 text-right">شماره</th><th className="px-4 py-3 text-center">وضعیت</th><th className="px-5 py-3 text-right">نتیجه</th></tr></thead><tbody className="divide-y divide-slate-100">{(campaign.recipients || []).map((recipient) => { const meta = statusStyle[recipient.status] || [recipient.status || "نامشخص", "bg-slate-100 text-slate-600"]; return <tr key={recipient.id}><td className="px-5 py-3 text-slate-800">{recipient.recipient_name || "شماره دستی"}</td><td className="px-4 py-3 text-slate-600" dir="ltr">{recipient.phone_number}</td><td className="px-4 py-3 text-center"><span className={"inline-flex rounded-full px-2.5 py-1 text-xs font-semibold " + meta[1]}>{meta[0]}</span></td><td className="max-w-xs px-5 py-3 text-xs text-slate-500">{recipient.error || recipient.provider_receipt_id || formatDateTime(recipient.sent_at)}</td></tr>; })}</tbody></table>
        </div>}
      </div>
    </div>
  );
}
